// Core lib dependencies.
import React, { useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * <pre>
 * PrivacyManagement React component.
 *
 * Display the coookie / privacy quick consent bar to the user. This is shown on the first visit ONLY
 * to our experiences, based on whether a consent cookie already exists.
 *
 * </pre>
 *
 * @memberof module:PrivacyManagement.Components
 * @namespace
 *
 * @return {PrivacyManagement.Components.PrivacyQuickConsentBar}
 */
const PrivacyQuickConsentBar = ({
  isVisible = false,
  onAcceptAll = () => {},
  onEssentialOnly = () => {},
  onOpenSettings = () => {},
}) => {
  // Get the privacy service.
  const privacyServiceRef = useRef(NGXD.getServiceContainer().getService('privacy'));

  const handleAcceptAll = () => {
    const userConsentChoices = privacyServiceRef.current.get();
    Object.keys(userConsentChoices).forEach((privacyType) => (userConsentChoices[privacyType] = true));
    privacyServiceRef.current.set(userConsentChoices);
    onAcceptAll();
  };

  const handleSettings = () => {
    onOpenSettings();
  };

  const handleEssentialOnly = () => {
    privacyServiceRef.current.set({});
    onEssentialOnly();
  };

  if (!isVisible) {
    return null;
  }

  return (
    // Wrapper added for aria screen readers
    <div className="xPrivacy_quickConsentBarWrapper" role="dialog">
      <div className="xPrivacy_quickConsentBar">
        <div className="xCell first extrawide xForm">
          <p className="xCopy info" tabIndex="-1">
            <span>{NGX.lang.privacy.consentBar.cookieStatement}</span>
          </p>
        </div>
        <div className="xCell xCTAContainer thin">
          <button className="xButton xCTA" aria-label={NGX.lang.privacy.consentBar.settings} onClick={handleSettings}>
            <span>{NGX.lang.privacy.consentBar.settings}</span>
          </button>
          <button
            className="xButton xCTA"
            aria-label={NGX.lang.privacy.consentBar.essentialOnly}
            onClick={handleEssentialOnly}
          >
            <span>{NGX.lang.privacy.consentBar.essentialOnly}</span>
          </button>
          <button className="xButton xCTA" aria-label={NGX.lang.privacy.consentBar.accept} onClick={handleAcceptAll}>
            <span>{NGX.lang.privacy.consentBar.accept}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Boolean} isVisible Should we show the privacy consent popup
 * @property {Function} onAcceptAll Function to call when handling the 'accept all' button click
 * @property {Function} onOpenSettings Function to call when handling the 'confirm my choices' button click
 */
PrivacyQuickConsentBar.propTypes = {
  isVisible:       PropTypes.bool,
  onAcceptAll:     PropTypes.func,
  onEssentialOnly: PropTypes.func,
  onOpenSettings:  PropTypes.func,
};

// Export the component as default.
export default PrivacyQuickConsentBar;
